import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';

const PaymentSettingsPanel = ({ selectedElement, onUpdateElement, paymentConfig, onUpdatePaymentConfig }) => {
  const [activeTab, setActiveTab] = useState('element');

  const tabs = [
    { id: 'element', name: 'Element', icon: 'Settings' },
    { id: 'payment', name: 'Payment', icon: 'CreditCard' },
    { id: 'security', name: 'Security', icon: 'Shield' }
  ];

  const currencyOptions = [
    { value: 'USD', label: 'USD - US Dollar' },
    { value: 'EUR', label: 'EUR - Euro' },
    { value: 'GBP', label: 'GBP - British Pound' },
    { value: 'CAD', label: 'CAD - Canadian Dollar' },
    { value: 'AUD', label: 'AUD - Australian Dollar' },
    { value: 'INR', label: 'INR - Indian Rupee' },
    { value: 'JPY', label: 'JPY - Japanese Yen' }
  ];

  const processorOptions = [
    { value: 'stripe', label: 'Stripe' },
    { value: 'paypal', label: 'PayPal' },
    { value: 'square', label: 'Square' },
    { value: 'razorpay', label: 'Razorpay' }
  ];

  const paymentModeOptions = [
    { value: 'fixed', label: 'Fixed Amount' },
    { value: 'variable', label: 'Variable Amount' },
    { value: 'subscription', label: 'Subscription' }
  ];

  const subscriptionIntervalOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' }
  ];

  const renderElementSettings = () => {
    if (!selectedElement) {
      return (
        <div className="text-center py-8">
          <Icon name="MousePointer" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">
            Select an element to configure its properties
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <Input
          label="Label"
          value={selectedElement?.label || ''}
          onChange={(e) => onUpdateElement({ ...selectedElement, label: e?.target?.value })}
          placeholder="Element label"
        />

        {selectedElement?.type === 'input' && (
          <Input
            label="Placeholder"
            value={selectedElement?.placeholder || ''}
            onChange={(e) => onUpdateElement({ ...selectedElement, placeholder: e?.target?.value })}
            placeholder="Placeholder text"
          />
        )}

        <Checkbox
          label="Required Field"
          checked={selectedElement?.required || false}
          onChange={(e) => onUpdateElement({ ...selectedElement, required: e?.target?.checked })}
          description="Make this field mandatory"
        />

        {selectedElement?.type === 'payment-amount' && (
          <>
            <Input
              label="Minimum Amount"
              type="number"
              value={selectedElement?.minAmount || 0}
              onChange={(e) => onUpdateElement({ ...selectedElement, minAmount: parseFloat(e?.target?.value) })}
              placeholder="0.00"
              step="0.01"
            />
            <Input
              label="Maximum Amount"
              type="number"
              value={selectedElement?.maxAmount || 10000}
              onChange={(e) => onUpdateElement({ ...selectedElement, maxAmount: parseFloat(e?.target?.value) })}
              placeholder="10000.00"
              step="0.01"
            />
          </>
        )}

        <div className="pt-4 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            iconName="Copy"
            className="w-full"
          >
            Duplicate Element
          </Button>
        </div>
      </div>
    );
  };

  const renderPaymentSettings = () => {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
            <p className="text-xs text-foreground">
              Configure global payment settings for this form
            </p>
          </div>
        </div>

        <Select
          label="Payment Processor"
          options={processorOptions}
          value={paymentConfig?.processor || 'stripe'}
          onChange={(value) => onUpdatePaymentConfig({ ...paymentConfig, processor: value })}
          description="Choose your payment gateway"
        />

        <Select
          label="Payment Mode"
          options={paymentModeOptions}
          value={paymentConfig?.mode || 'fixed'}
          onChange={(value) => onUpdatePaymentConfig({ ...paymentConfig, mode: value })}
          description="How customers will pay"
        />

        {paymentConfig?.mode === 'fixed' && (
          <Input
            label="Amount"
            type="number"
            value={paymentConfig?.amount || 0}
            onChange={(e) => onUpdatePaymentConfig({ ...paymentConfig, amount: parseFloat(e?.target?.value) })}
            placeholder="0.00"
            step="0.01"
            description="Fixed payment amount"
          />
        )}

        <Select
          label="Currency"
          options={currencyOptions}
          value={paymentConfig?.currency || 'USD'}
          onChange={(value) => onUpdatePaymentConfig({ ...paymentConfig, currency: value })}
          description="Payment currency"
        />

        <Input
          label="Payment Description"
          value={paymentConfig?.description || ''}
          onChange={(e) => onUpdatePaymentConfig({ ...paymentConfig, description: e?.target?.value })}
          placeholder="Payment for services"
          description="Appears on receipts"
        />

        {paymentConfig?.mode === 'subscription' && (
          <>
            <Select
              label="Billing Interval"
              options={subscriptionIntervalOptions}
              value={paymentConfig?.subscriptionInterval || 'monthly'}
              onChange={(value) => onUpdatePaymentConfig({ ...paymentConfig, subscriptionInterval: value })}
              description="Subscription frequency"
            />
            <Checkbox
              label="Enable Free Trial"
              checked={paymentConfig?.enableTrial || false}
              onChange={(e) => onUpdatePaymentConfig({ ...paymentConfig, enableTrial: e?.target?.checked })}
              description="Offer trial period before charging"
            />
          </>
        )}

        <div className="pt-4 border-t border-border space-y-3">
          <Checkbox
            label="Collect Billing Address"
            checked={paymentConfig?.collectBilling || true}
            onChange={(e) => onUpdatePaymentConfig({ ...paymentConfig, collectBilling: e?.target?.checked })}
            description="Required for payment processing"
          />
          <Checkbox
            label="Collect Shipping Address"
            checked={paymentConfig?.collectShipping || false}
            onChange={(e) => onUpdatePaymentConfig({ ...paymentConfig, collectShipping: e?.target?.checked })}
            description="For physical product delivery"
          />
        </div>

        <div className="p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">Payment Preview</span>
            <Icon name="Eye" size={16} className="text-muted-foreground" />
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Amount:</p>
            <p className="text-lg font-semibold text-foreground">
              {paymentConfig?.mode === 'fixed' ? new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: paymentConfig?.currency || 'USD',
                  })?.format(paymentConfig?.amount || 0)
                : paymentConfig?.mode === 'subscription'
                ? `Recurring ${paymentConfig?.subscriptionInterval || 'monthly'}`
                : 'Customer-defined'
              }
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderSecuritySettings = () => {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-start gap-2">
            <Icon name="ShieldCheck" size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-green-800 dark:text-green-200">
              <p className="font-medium mb-1">Secure Card Handling</p>
              <p>Card details are collected and processed by Stripe (PCI DSS Level 1); they never touch our servers</p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-md">
            <div className="flex items-center gap-2">
              <Icon name="Lock" size={16} className="text-green-600" />
              <span className="text-sm text-foreground">SSL Encryption</span>
            </div>
            <Icon name="CheckCircle2" size={16} className="text-green-600" />
          </div>

          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-md">
            <div className="flex items-center gap-2">
              <Icon name="Shield" size={16} className="text-green-600" />
              <span className="text-sm text-foreground">Tokenization</span>
            </div>
            <Icon name="CheckCircle2" size={16} className="text-green-600" />
          </div>

          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-md">
            <div className="flex items-center gap-2">
              <Icon name="Eye" size={16} className="text-green-600" />
              <span className="text-sm text-foreground">Fraud Detection</span>
            </div>
            <Icon name="CheckCircle2" size={16} className="text-green-600" />
          </div>
        </div>

        <Checkbox
          label="Enable 3D Secure"
          checked={paymentConfig?.enable3DSecure || true}
          onChange={(e) => onUpdatePaymentConfig({ ...paymentConfig, enable3DSecure: e?.target?.checked })}
          description="Additional authentication for card payments"
        />

        <Checkbox
          label="Require CVV"
          checked={paymentConfig?.requireCVV || true}
          onChange={(e) => onUpdatePaymentConfig({ ...paymentConfig, requireCVV: e?.target?.checked })}
          description="Mandatory CVV verification"
        />

        <div className="pt-4 border-t border-border">
          <h4 className="text-sm font-medium text-foreground mb-3">Compliance</h4>
          <div className="space-y-2 text-xs text-muted-foreground">
            <p>✓ Payments processed by Stripe (PCI DSS Level 1)</p>
            <p>✓ Card details never touch our servers</p>
            <p>✓ Encrypted in transit (TLS)</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-80 bg-card border-l border-border flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <h2 className="text-sm font-semibold text-foreground">Settings</h2>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`
                flex-1 flex items-center justify-center gap-2 px-4 py-3 text-xs font-medium transition-smooth border-b-2
                ${activeTab === tab?.id
                  ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }
              `}
            >
              <Icon name={tab?.icon} size={14} />
              <span>{tab?.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'element' && renderElementSettings()}
        {activeTab === 'payment' && renderPaymentSettings()}
        {activeTab === 'security' && renderSecuritySettings()}
      </div>
    </div>
  );
};

export default PaymentSettingsPanel;